import { Tooltip } from 'antd';
import classNames from 'classnames';
import { isEqual as _isEqual } from 'lodash';
import React from 'react';

import Icon from '@ant-design/icons';
import styles from '@components/summary-panel/FilterDropdown/FilterName/index.less';
import CloseFilterHoverActiveSvg from '@icons/close-filter-hover-active.svg';
import CloseFilterHoverSvg from '@icons/close-filter-hover.svg';
import CloseFilterSvg from '@icons/close-filter.svg';
import LinkFilterSvg from '@icons/link-filter.svg';
import { useIsNewWorkflowPage } from '@src/hooks/useNewWorkflow';

export interface IRule {
  display: string;
  field: string;
  key: string;
  op: string;
  originalField: string;
  value: string;
  _order: number;
}

interface IOnMouseOverAndOutFilter {
  e?: MouseEvent;
  rule: IRule;
}

interface IOnClickFilter {
  e: MouseEvent;
  rule: IRule;
  andIndex: number;
  orIndex: number;
}

interface IOrFilterName {
  activeRule?: IRule;
  changeActiveRule?: (rule?: IRule) => void;
  showCloseIcon?: boolean;
  firstOrFilterUnderAndFilterGroup?: boolean;
  andIndex: number;
  orIndex: number;
  prefix?: 'AND' | 'OR';
  rule: IRule;
  showLinkFilters?: boolean;
  onRemoveFilter?: ({ e, rule, andIndex, orIndex }: IOnClickFilter) => void;
  onMouseOverCloseIcon?: ({ e, rule }: IOnMouseOverAndOutFilter) => void;
  onMouseLeaveCloseIcon?: ({ e, rule }: IOnMouseOverAndOutFilter) => void;
}

const MAX_CHARACTER_LENGTH = 27;

const FilterNameItem: React.FC<IOrFilterName> = ({
  activeRule,
  changeActiveRule,
  showCloseIcon,
  firstOrFilterUnderAndFilterGroup,
  andIndex,
  orIndex,
  prefix,
  rule,
  showLinkFilters,
  onRemoveFilter,
  onMouseOverCloseIcon,
  onMouseLeaveCloseIcon,
}) => {
  const isEasierEventLooker = useIsNewWorkflowPage();
  const onMouseOver = (e) => {
    if (!_isEqual(activeRule, rule)) {
      changeActiveRule?.(rule);
      onMouseOverCloseIcon?.({ e, rule });
    }
  };

  const onMouseLeave = () => {
    changeActiveRule?.(undefined);
    onMouseLeaveCloseIcon?.({ rule });
  };

  const onClickCloseIcon = (e) => {
    // Delete does not trigger the leave event, manually trigger once
    onMouseLeave();
    onRemoveFilter && onRemoveFilter({ e, rule, andIndex, orIndex });
  };

  const FilterCloseIcon = () => (
    <div
      key={rule.value}
      className={styles.iconWrapper}
      onMouseOver={onMouseOver}
      onMouseLeave={onMouseLeave}
      onClick={onClickCloseIcon}
    >
      <Icon
        className={classNames(styles.commonIconStyle, styles.closeFilterIcon)}
        component={CloseFilterSvg}
      />
      <Icon
        className={classNames(styles.commonIconStyle, styles.closeFilterHoverIcon)}
        component={CloseFilterHoverSvg}
      />
      <Icon
        className={classNames(styles.commonIconStyle, styles.closeFilterHoverActiveIcon)}
        component={CloseFilterHoverActiveSvg}
      />
      <Icon
        className={classNames(styles.commonIconStyle, styles.linkFilterIcon)}
        component={LinkFilterSvg}
      />
    </div>
  );
  return (
    <div
      className={classNames(styles.orFilterWrapper, {
        [styles.firstOrFilterUnderAndFilterGroup]: firstOrFilterUnderAndFilterGroup,
        [styles.orFilterWrapperBlue]: isEasierEventLooker,
      })}
    >
      <div
        className={classNames(styles.orFilter, {
          [styles.orFilterWithLinkIcon]: showLinkFilters,
          [styles.orFilterBlue]: isEasierEventLooker,
        })}
      >
        <span className={styles.andOrText}>{prefix}</span>
        {rule.value.length > MAX_CHARACTER_LENGTH ? (
          <Tooltip title={rule.value} placement="bottomLeft">
            <span className={styles.filterNameItemText}>{rule.value}</span>
          </Tooltip>
        ) : (
          <span className={styles.filterNameItemText}>{rule.value}</span>
        )}
        {showCloseIcon && <FilterCloseIcon />}
      </div>
    </div>
  );
};

export default FilterNameItem;
